// @flow

import React from 'react'
import styled, { css } from 'styled-components'
import Colors from 'components/styles/colors'
import { mediaMax } from 'frontend_core/dist/styles/media'
import { fontSize, spacing } from 'frontend_core/dist/styles/variables'

const Status = styled.span`
  &:not(:first-of-type) {
    margin-left: 6px;
  }

  display: inline-block;
  border-radius: 3px;
  ${({ mainColor }) =>
    mainColor &&
    css`
      color: ${Colors.white};
      background-color: ${mainColor};
    `}
  ${({ labelWidthPc }) =>
    labelWidthPc &&
    css`
      min-width: ${labelWidthPc}px;
    `}
  ${({ labelWidthSp }) =>
    labelWidthSp &&
    css`
      ${mediaMax.phone`
      min-width: ${labelWidthSp}px;
    `};
    `}
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      min-width: 100%;
    `}
  ${({ labelHeightPc }) =>
    labelHeightPc &&
    css`
      height: ${labelHeightPc}px;
      line-height: ${labelHeightPc}px;
    `}
  ${({ labelHeightSp }) =>
    labelHeightSp &&
    css`
      ${mediaMax.phone`
      height: ${labelHeightSp}px;
      line-height: ${labelHeightSp}px;
    `};
    `}
  ${({ border }) =>
    border &&
    css`
      ${({ mainColor }) =>
        mainColor &&
        css`
          border: ${({ boldBorder }) => (boldBorder ? '1.5px' : '1px')} solid
            ${mainColor};
        `};
    `}
  ${({ bottomBorder }) =>
    bottomBorder &&
    css`
      ${({ subColor }) =>
        subColor &&
        css`
          border-bottom: ${({ boldBorder }) => (boldBorder ? '1.5px' : '1px')}
            solid ${subColor};
        `};
    `}
  ${({ onlyBorder }) =>
    onlyBorder &&
    css`
      ${({ mainColor }) =>
        mainColor &&
        css`
          color: ${mainColor};
          background-color: ${Colors.white};
          border: ${({ boldBorder }) => (boldBorder ? '1.5px' : '1px')} solid
            ${mainColor};
        `};
    `}
  ${({ fontsize }) =>
    fontsize &&
    css`
      font-size: ${fontsize}px;
    `}
  ${({ fontsizeSp }) =>
    fontsizeSp &&
    css`
      ${mediaMax.phone`
      font-size: ${fontsizeSp}px;
    `};
    `}
  ${({ boldFont }) =>
    boldFont &&
    css`
      font-weight: bold;
    `}
  ${({ paddingVertical, paddingHorizontal }) => css`
    padding: ${paddingVertical || 0}px ${paddingHorizontal || 0}px;
  `}
  ${({ bold }) =>
    bold &&
    css`
      font-weight: bold;
    `}
`

const Content = styled.span`
  display: block;
  vertical-align: middle;
  width: 100%;
  text-align: center;
`

export type PropTypes = {
  labelWidthPc?: number,
  labelWidthSp?: number,
  labelHeightPc?: number,
  labelHeightSp?: number,
  fullWidth?: boolean,
  labelMargin?: number,
  mainColor?: string,
  subColor?: string,
  fontsize?: string,
  fontsizeSp?: number,
  border?: boolean,
  bottomBorder?: boolean,
  onlyBorder?: boolean,
  boldFont?: boolean,
  boldBorder?: boolean,
  children?: string,
}

const baseProps = {
  labelWidthPc: 78,
  labelHeightPc: 24,
  fontsize: fontSize.small,
  onlyBorder: true,
  paddingHorizontal: spacing.small,
}

const BaseStatus = (props: PropTypes) => (
  <Status {...props}>
    <Content>{props.children}</Content>
  </Status>
)

const HourlyPrice = () => (
  <BaseStatus {...baseProps} mainColor={Colors.black} subColor={Colors.black}>
    {i18n.t('page.shared.hourlyPrice')}
  </BaseStatus>
)

const AllDayPrice = () => (
  <BaseStatus {...baseProps} mainColor={Colors.black} subColor={Colors.black}>
    {i18n.t('page.shared.allDayPrice')}
  </BaseStatus>
)

const RentalSmall = () => (
  <BaseStatus
    {...baseProps}
    mainColor={Colors.blue}
    subColor={Colors.stayLightBlue}
  >
    {i18n.t('page.shared.rental')}
  </BaseStatus>
)

const StaySmall = () => (
  <BaseStatus
    {...baseProps}
    mainColor={Colors.stayPink}
    subColor={Colors.stayBdColor}
  >
    {i18n.t('page.shared.stay')}
  </BaseStatus>
)

const ReservedBlue = () => (
  <BaseStatus {...baseProps} mainColor={Colors.blue} subColor={Colors.blue}>
    {i18n.t('page.shared.reservation')}
  </BaseStatus>
)

const ReservedPink = () => (
  <BaseStatus
    {...baseProps}
    mainColor={Colors.stayPink}
    subColor={Colors.stayBdColor}
  >
    {i18n.t('page.shared.reservation')}
  </BaseStatus>
)

const Scheduled = () => (
  <BaseStatus {...baseProps} mainColor={Colors.blue} subColor={Colors.blue}>
    {i18n.t('page.shared.schedule')}
  </BaseStatus>
)

const ReserveNotAccept = () => (
  <BaseStatus {...baseProps} mainColor={Colors.black} subColor={Colors.black}>
    {i18n.t('page.shared.reserveNotAccept')}
  </BaseStatus>
)

const RentalLarge = () => (
  <BaseStatus
    {...baseProps}
    labelWidthPc={100}
    labelHeightPc={40}
    mainColor={Colors.blue}
    subColor={Colors.stayLightBlue}
    fontsize={fontSize.small_3}
    onlyBorder
    boldFont
  >
    {i18n.t('page.shared.rental')}
  </BaseStatus>
)

const StayLarge = () => (
  <BaseStatus
    {...baseProps}
    labelWidthPc={100}
    labelHeightPc={40}
    mainColor={Colors.stayPink}
    subColor={Colors.stayPink}
    fontsize={fontSize.small_3}
    onlyBorder
    boldFont
  >
    {i18n.t('page.shared.stay')}
  </BaseStatus>
)

const onlyBorderFullWidth = (props: PropTypes) => (
  <BaseStatus
    labelHeightPc={30}
    labelHeightSp={40}
    fontsize={fontSize.small_2}
    fontsizeSp={13}
    fullWidth
    onlyBorder
    mainColor={Colors.blue}
  >
    {props.children}
  </BaseStatus>
)

const onlyBorderFullWidthLarge = (props: PropTypes) => (
  <BaseStatus
    labelHeightPc={40}
    fontsize={fontSize.small_3}
    bold
    fullWidth
    onlyBorder
    mainColor={Colors.blue}
  >
    {props.children}
  </BaseStatus>
)

const CoverPhoto = ({ children }) => (
  <BaseStatus
    mainColor={Colors.blue}
    subColor={Colors.blue}
    fontsize={fontSize.small_2}
    onlyBorder
    paddingVertical={spacing.small}
    paddingHorizontal={spacing.small_3}
  >
    {children}
  </BaseStatus>
)

const Recommended = () => (
  <BaseStatus
    {...baseProps}
    labelWidthPc={60}
    labelHeightPc={23}
    fontsize={fontSize.small}
    mainColor={Colors.blue}
    subColor={Colors.blue}
  >
    {i18n.t('page.shared.recommend')}
  </BaseStatus>
)

export default {
  BaseStatus,
  HourlyPrice,
  AllDayPrice,
  StaySmall,
  RentalSmall,
  ReservedBlue,
  ReservedPink,
  Scheduled,
  ReserveNotAccept,
  RentalLarge,
  StayLarge,
  onlyBorderFullWidth,
  onlyBorderFullWidthLarge,
  CoverPhoto,
  Recommended,
}



// WEBPACK FOOTER //
// ./src/components/atoms/Status/index.jsx